import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { QaConsultant } from '../../_models/qa/qa-consultant.model';
import { QaReportRow, QaReportSearchRequest, QaReportType } from '../../_models/qa/qa-report-row.model';
import { QaApproveItem, QaApproveRequest, QaApproveResult } from '../../_models/qa/qa-approve.model';

/**
 * Typed calls to the QA report-approval endpoints (SDD/specs/QAMassApprove.md).
 *
 * The routes live on SessionController, not a QaController — the spec's §4.1 table
 * (api/Qa/...) describes a shape that was never built.
 *
 * Follows the shared BaseService pattern. prepareOptions() hashes
 * JSON.stringify(payload).toLowerCase() into the RequestMatch header, so the object
 * passed to prepareOptions MUST be the same object passed as the request body —
 * wrapping it (prepareOptions({ payload })) hashes a different shape and always 403s.
 * prepareOptions() also sets observe: 'response', so every response needs unwrap().
 */
@Injectable()
export class QaService extends BaseService {

    private apiUrl: string = environment.apiUrl;

    constructor(
        private http: HttpClient,
        protected hashManagerService: SessionUserHashManagerService
    ) {
        super(hashManagerService);
        this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
    }

    /** The tuition consultants that currently have FES reports awaiting QA. */
    public getConsultants(): Observable<QaConsultant[]> {
        // GET sends no body, so the hash must cover the empty-body placeholder (prepareOptions(null)).
        return this.http.get(this.apiUrl + 'api/Session/Qa/consultants', this.prepareOptions(null)).pipe(
            map((data: any) => this.unwrap(data) as QaConsultant[]),
            catchError(this.handleError('getQaConsultants'))
        );
    }

    /** Submitted FES reports for the selected consultants and report types, ordered by created date. */
    public searchReports(consultantIds: number[], reportTypes: QaReportType[]): Observable<QaReportRow[]> {
        const payload: QaReportSearchRequest = { consultantIds: consultantIds, reportTypes: reportTypes };
        return this.http.post(this.apiUrl + 'api/Session/Qa/reports/search', payload, this.prepareOptions(payload)).pipe(
            map((data: any) => this.unwrap(data) as QaReportRow[]),
            catchError(this.handleError('searchQaReports'))
        );
    }

    /**
     * Moves the given reports Submitted -> Approved. `items` is the snapshot the user saw and
     * left checked — never a re-query, so reports submitted since the search are excluded.
     */
    public approveReports(items: QaApproveItem[]): Observable<QaApproveResult> {
        const payload: QaApproveRequest = { items: items };
        return this.http.post(this.apiUrl + 'api/Session/Qa/reports/approve', payload, this.prepareOptions(payload)).pipe(
            map((data: any) => this.unwrap(data) as QaApproveResult),
            catchError(this.handleError('approveQaReports'))
        );
    }

    /** Shared body unwrap — prepareOptions() observes the full response, not just the body. */
    private unwrap(data: any): any {
        return data ? data.body : data;
    }
}
